import { type CreateUserPersonalDataRequestWithAreaOfInterestDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useApiClient } from '@/api/util/useApiClient.util.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type AxiosError } from 'axios';

interface UseCreateUserPersonalDataReturn {
  createUserPersonalDataWithInterests: (
    variables: {
      data: CreateUserPersonalDataRequestWithAreaOfInterestDto;
      userId: string;
    },
    options?: {
      onError?: (error: AxiosError) => void;
      onSuccess?: () => void;
    },
  ) => void;
  error: AxiosError | null;
  isLoading: boolean;
}

export const useCreateUserPersonalData = (): UseCreateUserPersonalDataReturn => {
  const { restUserApi } = useApiClient();
  const queryClient = useQueryClient();

  const {
    error,
    isPending: isLoading,
    mutate: createUserPersonalDataWithInterests,
  } = useMutation<
    void,
    AxiosError,
    {
      data: CreateUserPersonalDataRequestWithAreaOfInterestDto;
      userId: string;
    }
  >({
    mutationFn: async ({ data, userId }) => {
      await restUserApi.createUserPersonalData(userId, data);
    },
    onError: error => {
      console.error('Error creating user personal data:', error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.PROCESS],
      });
    },
  });

  return { createUserPersonalDataWithInterests, error, isLoading };
};
